import { FC, useContext, useMemo, useRef, useState, useEffect, JSX } from "react";
import { EcdReportGenerationWizardContextSteps } from "./interfaces";
import EcdGenerateReportContextProvider, { EcdGenerateReportContext } from "./contexts/EcdGenerateReportContext";
import { Link } from "react-router-dom";
import { Page, PageInner, LoadingSpinner, Breadcrumbs } from "../../shared";
import { WizardSteps, WizardNavigationBar, QESButton } from "../../shared/qes-standards";
import EcdReportGenerationExportDetails from "./components/EcdReportGenerationExportDetails";
import EcdReportGenerationExportPreview from "./components/EcdReportGenerationExportPreview";
import { focusFirstInteractiveElement } from "../../shared/utility";
import jsPDF from "jspdf";

const EcdReportGenerationWizard: FC = () => {
  return (
    <EcdGenerateReportContextProvider>
      <EcdReportGenerationWizardContent />
    </EcdGenerateReportContextProvider>
  );
}

const EcdReportGenerationWizardContent = (): JSX.Element => {
  const wizardContext = useContext(EcdGenerateReportContext);
  const wizardBodyRef = useRef<HTMLDivElement>(null);
  const [pageIsValid, setPageIsValid] = useState(false);

  useEffect(() => {
    focusFirstInteractiveElement(wizardBodyRef);
  }, [wizardContext.page]);

  const doc = new jsPDF('p', 'in', [8.5, 11], true);

  const humanReadablePageNames = {
    [EcdReportGenerationWizardContextSteps.ExportDetails]: "Export Details",
    [EcdReportGenerationWizardContextSteps.ExportPreview]: "Export Preview",
  };

  const getCurrentStep = () => {
    return wizardContext.page;
  }

  const handleUpdatePage = (newPage: number) => {
    const step = Object.entries(EcdReportGenerationWizardContextSteps).find((entry) => parseInt(entry[0], 10) === newPage)[1] as EcdReportGenerationWizardContextSteps;
    wizardContext.setPage(EcdReportGenerationWizardContextSteps[step] as unknown as EcdReportGenerationWizardContextSteps);
  };

  const goToPreviousPage = () => {
    if (wizardContext.page == EcdReportGenerationWizardContextSteps.ExportPreview)
      wizardContext.setPage(EcdReportGenerationWizardContextSteps.ExportDetails);
  };

  const validateAndAdvance = () => {
    if (wizardContext.page == EcdReportGenerationWizardContextSteps.ExportDetails) {
      wizardContext.setPage(EcdReportGenerationWizardContextSteps.ExportPreview);
    }
  };

  const handleSubmit = async () => {
    setPageIsValid(false)
    Promise.all(
      [
        wizardContext.getHtmlCanvasElement('.FiltersPage', wizardContext.includeFilterPage),
        wizardContext.getHtmlCanvasElement('.AdequacyBreakdown', wizardContext.includeAdequacyBreakdown),
        wizardContext.getHtmlCanvasElement('.GapOverview', wizardContext.includeGapOverview),
        wizardContext.getHtmlCanvasElement('.CompositeFillableGaps', wizardContext.includeCompositeFillableGaps),
        wizardContext.getHtmlCanvasElement('.GapsByAge', wizardContext.includeGapsByAge),
        wizardContext.getHtmlCanvasElement('.Exceptions', wizardContext.includeExceptions),
        wizardContext.getHtmlCanvasElement('.TopSpecialties', wizardContext.includeTopSpecialties),
        wizardContext.getHtmlCanvasElement('.TopCounties', wizardContext.includeTopCounties),
        wizardContext.getHtmlCanvasElement('.LineOfBusiness', wizardContext.includeLineOfBusiness),
        wizardContext.getHtmlCanvasElement('.CountyStatus', wizardContext.includeCountyStatus)
      ])
      .then(([filtersCanvas, adequacyCanvas, gapOverviewCanvas, compositeCanvas, gapsAgeCanvas, exceptionsCanvas, specialtiesCanvas,
        countiesCanvas, lineOfBusinessCanvas, countyStatusCanvas]) => {
        addPage(filtersCanvas);
        addPage(adequacyCanvas);
        addPage(gapOverviewCanvas);
        addPage(compositeCanvas);
        addPage(gapsAgeCanvas);
        addPage(exceptionsCanvas);
        addPage(specialtiesCanvas);
        addPage(countiesCanvas);
        addPage(lineOfBusinessCanvas);
        addPage(countyStatusCanvas);

        //don't know what the first and last page may be, so just add a page after every call and delete excess page
        doc.deletePage(doc.getNumberOfPages());
        doc.save(wizardContext.fileName);
      });
  }

  const currentWizardStep = useMemo(() => {
    switch (wizardContext.page) {
      case EcdReportGenerationWizardContextSteps.ExportPreview:
        return <EcdReportGenerationExportPreview pageIsValid={setPageIsValid} />;
      case EcdReportGenerationWizardContextSteps.ExportDetails:
        return <EcdReportGenerationExportDetails pageIsValid={setPageIsValid} />;
    }
  }, [wizardContext.page]);

  const addPage = (canvas: HTMLCanvasElement) => {
    if (canvas != null) {
      doc.addImage(canvas, 'PNG', 0, 0, 8.5, 11, undefined, 'NONE');
      doc.addPage();
    }
  }

  return (
    <Page documentTitle="Enterprise Compliance Dashboard Export">
      <Breadcrumbs
        items={[
          {
            link: "/overviews/enterprise-compliance-dashboard",
            text: "Enterprise Compliance Dashboard",
          },
          {
            text: "Export Format",
          },
        ]}
      />
      <PageInner className="create-exceptions-wizard">
        <header className="page-title-bar row justify-content-between align-items-center mt-4 mb-3">
          <h1 className="page-title mt-0 mb-0">Enterprise Compliance Dashboard Export</h1>
        </header>

        <WizardSteps currentStep={getCurrentStep()} steps={Object.values(humanReadablePageNames)} setStep={handleUpdatePage} />

        <div ref={wizardBodyRef}>
          {wizardContext.loading ?
            !!wizardContext.error ?
              null :
              <LoadingSpinner /> :
            currentWizardStep}
        </div>
      </PageInner>

      <WizardNavigationBar
        leftSide={(
          <Link to="/overviews/enterprise-compliance-dashboard" className="qes-button qes-button-secondary danger" data-testid="edc-report-generation-wizard__cancel">Cancel</Link>
        )}

        rightSide={(
          <div className="edc-report-generation-wizard__nav-buttons">
            {wizardContext.page !== EcdReportGenerationWizardContextSteps.ExportDetails &&
              <QESButton qesType="secondary" onClick={goToPreviousPage} qesTestId="edc-report-generation-wizard__previous">Previous</QESButton>}
            {wizardContext.page === EcdReportGenerationWizardContextSteps.ExportPreview ? (
              <QESButton
                disabled={!pageIsValid}
                qesType="primary"
                qesTestId="edc-report-generation-wizard__submit"
                className="ml-3"
                onClick={handleSubmit}>
                Export
              </QESButton>
            ) : (
              <QESButton
                onClick={validateAndAdvance}
                disabled={!pageIsValid}
                qesTestId="edc-report-generation-wizard__next"
                className="ml-3">
                Next
              </QESButton>
            )}
          </div>
        )}
      />
    </Page>
  );
}

export default EcdReportGenerationWizard;